import React from 'react';
import {
  EuiButton,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle, 
  EuiOverlayMask,
} from '@elastic/eui';

import ContentPanel from '../../../../Components/ContentPanel/ContentPanel';
import { ClusterDataProvider } from './ClusterDataProvider';
import { ClusterOverview } from './ClusterOverview';
import { ComponentDetails } from './ComponentDetails';
import { NodeDetails } from './NodeDetails';
import { SoftwareUpdateDetails } from '../../SoftwareUpdateOptions/Details/SoftwareUpdateDetails';
import { ClusterAuditHistory } from '../ClusterAuditHistory';
import { StackDetails } from '../../Stack/StackDetails';
import { ComponentVersionDetails } from '../../ComponentVersion/ComponentVersionList';
import { ComponentVersionDescribe } from '../../ComponentVersion/ComponentVersionDescribe';
import { ComponentDetails as ComponentDetailsModal } from '../../Component/V2/ComponentDetails';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';
import { NodeOperationDetails } from '../../NodeOperation/Details/NodeOperationDetails';
import { ComponentReleaseTrackerDetail } from '../../ComponentReleaseTracker/Details/ComponentReleaseTrackerDetail';
import { SoftwareUpdateTrackerDetails } from '../../SoftwareUpdateTracker/Details/SoftwareUpdateTrackerDetails';

// eslint-disable-next-line react/prefer-stateless-function
export class ClusterDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clusterSyncStatus: 'loading',
      showModal: false,
      modalContent: null,
      modalTitle: '',
    };
  }

  closeModal = () => {
    this.setState({
      modalContent: null,
      showModal: false,
      modalTitle: '',
    });
  };

  showStackDetailsModal = () => {
    this.setState({
      modalContent: <StackDetails stackName={this.props.stackName} />,
      showModal: true,
      modalTitle: 'Stack Details',
    });
  };

  showComponentVersionListModal = (component) => {
    this.setState({
      modalContent: (
        <ComponentVersionDetails
          stackName={this.props.stackName}
          componentName={component.componentName}
          componentScope={component.componentScopeDefinition.componentScope}
          componentScopeValue={
            component.componentScopeDefinition.componentScopeValue
          }
        />
      ),
      showModal: true,
      modalTitle: 'Component Version List',
    });
  };

  showComponentDetailsModal = (component) => {
    this.setState({
      modalContent: (
        <ComponentDetailsModal
          stackName={this.props.stackName}
          componentName={component.componentName}
          componentScope={component.componentScopeDefinition.componentScope}
          componentScopeValue={
            component.componentScopeDefinition.componentScopeValue
          }
        />
      ),
      showModal: true,
      modalTitle: 'Component Details',
    });
  };

  showComponentVersionDescribeModal = (component) => {
    this.setState({
      modalContent: (
        <ComponentVersionDescribe
          stackName={this.props.stackName}
          componentName={component.componentName}
          majorVersion={component.majorVersion}
          minorVersion={component.minorVersion}
          componentScope={component.componentScopeDefinition.componentScope}
          componentScopeValue={component.componentScopeDefinition.componentScopeValue}
        />
      ),
      showModal: true,
      modalTitle: `Component Version Details - ${component.componentName}`,
    });
  };

  setClusterSyncStatus = (status) => {
    this.setState({ clusterSyncStatus: status });
  };

  clusterOverviewActions = () => {
    return (
      <EuiFlexGroup>
        <EuiFlexItem>
          <div />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButtonIcon
            size="m"
            onClick={() => this.clusterOverviewRef?.refreshClusterData()}
            iconType="refresh"
          >
          </EuiButtonIcon>
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  };

  renderClusterOverview = () => {
    const { stackName, clusterId } = this.props;
    const { clusterSyncStatus } = this.state;
    return (
      <ContentPanel
        actions={this.clusterOverviewActions()}
        title={'Cluster Details'}
      >
        <ClusterOverview 
          ref={ref => this.clusterOverviewRef = ref}
          stackName={stackName}
          clusterId={clusterId}
          clusterSyncStatus={clusterSyncStatus}
          showStackDetailsModal={this.showStackDetailsModal}
          showComponentVersionListModal={this.showComponentVersionListModal}
          showComponentDetailsModal={this.showComponentDetailsModal}
          showComponentVersionDescribeModal={this.showComponentVersionDescribeModal}
        />
      </ContentPanel>
    );
  };

  renderNodeDetailsTable = () => {
    const { clusterSyncStatus } = this.state;
    const { stackName, clusterId } = this.props;
    return (
      <NodeDetails
        setClusterSyncStatus={this.setClusterSyncStatus}
        clusterSyncStatus={clusterSyncStatus}
        stackName={stackName}
        clusterId={clusterId}
      />
    );
  };

  renderSoftwareUpdateOptions = () => {
    return (
      <ContentPanel title={'Software Update Details'}>
        <SoftwareUpdateDetails
          stackName={this.props.stackName}
          clusterId={this.props.clusterId}
        />
      </ContentPanel>
    );
  };

  renderComponentReleaseTrackerTable = () => {
    return (
      <ContentPanel title={'Component Release Tracker Details'}>
        <ComponentReleaseTrackerDetail
          stackName={this.props.stackName}
        />
      </ContentPanel>
    );
  };

  renderSoftwareUpdateTrackerTable = () => {
    return (
      <ContentPanel
        title={'Software Update Tracker Details'}
        isCollapsible={true}
        initialIsOpen={false}
      >
        <SoftwareUpdateTrackerDetails
          stackName={this.props.stackName}
          isSDPDSMetric={false}
        />
      </ContentPanel>
    );
  };

  componentDetailsActions = () => {
    return (
      <EuiFlexGroup>
        <EuiFlexItem>
          <div />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButtonIcon
            size="m"
            onClick={() => this.componentDetailsRef?.refreshClusterData()}
            iconType="refresh"
          >
          </EuiButtonIcon>
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  };

  renderComponentDetails = () => {
    const { stackName, clusterId } = this.props;
    return (
      <ContentPanel
        actions={this.componentDetailsActions()}
        title={'Component Details'}
      >
        <ComponentDetails 
          ref={ref => this.componentDetailsRef = ref}
          stackName={stackName}
          clusterId={clusterId}
          showComponentVersionListModal={this.showComponentVersionListModal}
          showComponentDetailsModal={this.showComponentDetailsModal}
          showComponentVersionDescribeModal={this.showComponentVersionDescribeModal}
        />
      </ContentPanel>
    );
  };

  renderNodeOperationDetails = () => {
    return (
      <ContentPanel title={'Node Operation Details'}>
        <NodeOperationDetails
          stackName={this.props.stackName}
          clusterId={this.props.clusterId}
        />
      </ContentPanel>
    );
  };

  renderModal = () => {
    const { state, closeModal } = this;
    const { showModal, modalContent, modalTitle } = state;
    if (showModal && modalContent) {
      return (
        <EuiOverlayMask>
          <EuiModal key={'modal'} onClose={closeModal}>
            <ElementErrorBoundary key={'modal-error-boundary'}>
              <EuiModalHeader>
                <EuiModalHeaderTitle>
                  <h2>{modalTitle}</h2>
                </EuiModalHeaderTitle>
              </EuiModalHeader>
              <EuiModalBody>{modalContent}</EuiModalBody>
              <EuiModalFooter>
                <EuiButton onClick={closeModal} fill>
                  Close
                </EuiButton>
              </EuiModalFooter>
            </ElementErrorBoundary>
          </EuiModal>
        </EuiOverlayMask>
      );
    }
    return null;
  };

  render() {
    const { stackName, clusterId } = this.props;

    return (
      <ElementErrorBoundary key={'tumbler-cluster-details'}>
        {this.renderModal()}
        <ClusterDataProvider stackName={stackName} clusterId={clusterId}>
          <ElementErrorBoundary key={'clusterOverview'}>
            {this.renderClusterOverview()}
          </ElementErrorBoundary>
          <br />
          <ElementErrorBoundary key={'componentDetails'}>
            {this.renderComponentDetails()}
          </ElementErrorBoundary>
        </ClusterDataProvider>
        <br/>
        <ElementErrorBoundary>
          {this.renderNodeOperationDetails()}
        </ElementErrorBoundary>
        <br />
        <ElementErrorBoundary key={'node-details'}>
          {this.renderNodeDetailsTable()}
        </ElementErrorBoundary>
        <br />
        <ElementErrorBoundary key={'software-update-options'}>
          {this.renderSoftwareUpdateOptions()}
        </ElementErrorBoundary>
        <br />
        <ElementErrorBoundary>
          {this.renderComponentReleaseTrackerTable()}
        </ElementErrorBoundary>
        <br />
        <ElementErrorBoundary>
          {this.renderSoftwareUpdateTrackerTable()}
        </ElementErrorBoundary>
        <br />
        <ElementErrorBoundary key={'audit-history'}>
          <ClusterAuditHistory stackName={stackName} clusterId={clusterId} />
        </ElementErrorBoundary>
      </ElementErrorBoundary>
    );
  }
}
